import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const reasons = [
  {
    icon: 'ri-lock-unlock-line',
    title: 'No Lock-In Contracts',
    body: "Month-to-month. If we're not delivering results, you walk — no penalties, no awkward conversations. We're confident enough in our work that we don't need you trapped.",
    accent: 'ds-red',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Tourism Industry Expertise',
    body: "We specialise in tourism and hospitality — reef trips, ferry services, adventure charters, dive operators, accommodation. We understand seasonal bookings, aggregator competition, and how to get a tourism business cited by AI when a traveller asks ChatGPT who to call in your area.",
    accent: 'ds-red',
  },
  {
    icon: 'ri-bar-chart-rising-line',
    title: 'Results-First Approach',
    body: "Every strategy, every decision starts with one question: does this make your business more money? No vanity metrics, no fluff — we measure what actually matters.",
    accent: 'ds-red',
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Custom Everything',
    body: "No templates. No drag-and-drop builders. No copy-paste strategies. Your website, your AI setup, your SEO plan — built from scratch around your brand and your goals.",
    accent: 'ds-red',
  },
  {
    icon: 'ri-chat-check-line',
    title: 'Straight Talking Only',
    body: "We don't hide behind jargon or drown you in meaningless reports. Clear, plain-English updates on what's working, what isn't, and exactly what we're doing about it.",
    accent: 'ds-red',
  },
  {
    icon: 'ri-shield-user-line',
    title: "We're In Your Corner",
    body: "We treat your business like it's our own. Your market share is our mission. When you win, we win — and that's the only kind of partnership we know how to do.",
    accent: 'ds-red',
  },
];

const resultBadges = [
  { num: '#1', label: 'Google Ranking' },
  { num: '4.2x', label: 'Booking Enquiries' },
  { num: '60%', label: 'After-Hours Leads Captured' },
  { num: 'Fully Booked', label: 'Most Weekends' },
];

export default function WhyDarksideSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="why-darkside" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      {/* Top accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red to-transparent" />
      {/* Grid overlay */}
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      {/* Left ambient glow */}
      <div className="absolute top-1/3 left-0 w-[500px] h-[500px] bg-ds-red/6 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16 reveal">
          <span className="section-label">Why Us</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-none">
              WHY
              <br />
              <span className="text-ds-red neon-text-red">DARKSIDE?</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-sm lg:text-right">
              Plenty of agencies will take your money. Not many will take your growth personally.
              <br />
              <span className="text-white/80">Here&apos;s the difference.</span>
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red" style={{ boxShadow: '0 0 12px rgba(255,23,68,0.5)' }} />
        </div>

        {/* Reasons grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ds-border mb-16 reveal">
          {reasons.map((r, i) => (
            <div
              key={r.title}
              className="group bg-ds-bg-2 p-8 flex flex-col gap-5 hover:bg-ds-bg-3 transition-colors duration-300 cursor-default"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 border border-ds-border group-hover:border-ds-red/50 transition-colors duration-300">
                  <i className={`${r.icon} text-ds-red text-xl`} />
                </div>
                <div className="pt-1">
                  <h3 className="font-display text-base text-white tracking-wider leading-tight">
                    {r.title}
                  </h3>
                </div>
              </div>
              <div className="h-px bg-ds-border group-hover:bg-ds-red/20 transition-colors duration-300" />
              <p className="font-body text-white/50 text-sm leading-relaxed group-hover:text-white/70 transition-colors duration-300">
                {r.body}
              </p>
            </div>
          ))}
        </div>

        {/* Transformation Card */}
        <div className="reveal">
          <div
            className="border border-ds-red/30 bg-ds-bg p-8 lg:p-12"
            style={{ borderLeft: '3px solid #B4FF39', boxShadow: '0 0 60px rgba(180,255,57,0.05)' }}
          >
            {/* Label */}
            <span className="font-body text-ds-red text-xs tracking-[0.3em] uppercase mb-5 block">
              Real Result
            </span>

            {/* Headline */}
            <h3
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-none mb-10"
              style={{ letterSpacing: '0.03em' }}
            >
              52 DAYS. ZERO ONLINE PRESENCE
              <br />
              <span className="text-ds-red neon-text-red">TO #1 ON GOOGLE.</span>
            </h3>

            {/* Stat badges */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-ds-border mb-8">
              {resultBadges.map((s) => (
                <div key={s.label} className="bg-ds-bg-2 px-6 py-5 text-center">
                  <div
                    className="font-display text-3xl text-ds-red leading-none mb-1.5"
                    style={{ letterSpacing: '0.03em' }}
                  >
                    {s.num}
                  </div>
                  <div className="font-body text-white/55 text-xs tracking-[0.15em] uppercase leading-snug">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Attribution */}
            <p className="font-body text-white/35 text-xs tracking-wider mb-8">
              Mission Beach Dive — Tourism operator, Mission Beach QLD
            </p>

            {/* CTA */}
            <a href="#contact" className="btn-red inline-flex whitespace-nowrap">
              <span>Get Your Free AI Visibility Audit</span>
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>

        {/* Bottom manifesto strip */}
        <div
          className="mt-16 p-6 lg:p-10 border border-ds-border bg-ds-bg text-center reveal"
        >
          <p className="font-display text-xl sm:text-2xl md:text-3xl text-white/90 leading-snug">
            We don&apos;t take on every client.
            <br />
            <span className="text-ds-red">We take on the right ones.</span>
          </p>
          <p className="font-body text-white/40 text-sm mt-4 max-w-lg mx-auto leading-relaxed">
            If you&apos;re serious about growth, not just looking for a cheap website,
            let&apos;s talk. If it&apos;s not a fit, we&apos;ll tell you straight.
          </p>
        </div>

      </div>
    </section>
  );
}
